<app-navbar />

<main id="main-content">
  <section class="page-hero">
    <div class="container">
      <app-breadcrumb [trail]="[{ label: 'Developers' }]" />
      <span class="eyebrow">Developers</span>
      <h1>A clean REST API for deceased verification</h1>
      <p>
        Verify a single identity in real time or submit large batches. Predictable JSON, clear
        status codes and a confidence score on every match. Get running in an afternoon.
      </p>
      <div class="actions-row">
        <a class="btn btn-primary btn-lg" [routerLink]="[routes.contact.url]">Request API access</a>
        <a class="btn btn-outline btn-lg" [routerLink]="[routes.pricing.url]">View pricing</a>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="grid-3">
        <div class="feature-card">
          <h3>Real time</h3>
          <p>Single verification returns in milliseconds so you can screen inside an application flow.</p>
        </div>
        <div class="feature-card">
          <h3>Batch ready</h3>
          <p>Submit up to 1,000 records per request or upload a file for very large jobs.</p>
        </div>
        <div class="feature-card">
          <h3>Confidence scored</h3>
          <p>Every match includes a confidence score so you can set your own decision thresholds.</p>
        </div>
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Endpoints</span>
        <h2 class="section-title">Core API endpoints</h2>
      </div>
      <div class="card endpoint-card">
        <table class="data-table endpoint-table">
          <tbody>
            @for (endpoint of endpoints; track endpoint.path) {
              <tr>
                <td><span class="method">{{ endpoint.method }}</span></td>
                <td><code>{{ endpoint.path }}</code></td>
                <td class="muted endpoint-desc">{{ endpoint.description }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Authentication</span>
        <h2 class="section-title">Authenticate with a bearer token</h2>
        <p class="section-lead">
          Every request uses your secret API key as a bearer token. Keys are issued per environment
          so you can keep test and production traffic separate. Never expose a secret key in client
          side code.
        </p>
      </div>
      <div class="code-block">
        <div class="code-head">Authorization header</div>
        <pre><code>Authorization: Bearer YOUR_API_KEY</code></pre>
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Verify a single identity</span>
        <h2 class="section-title">Request and response</h2>
      </div>
      <div class="grid-2 align-start">
        <div class="code-block">
          <div class="code-head">Request</div>
          <pre><code>{{ requestSample }}</code></pre>
        </div>
        <div class="code-block">
          <div class="code-head">Response</div>
          <pre><code>{{ responseSample }}</code></pre>
        </div>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Request fields</span>
        <h2 class="section-title">What to send</h2>
        <p class="section-lead">Name is required. The more identifiers you send, the higher the match confidence.</p>
      </div>
      <div class="card">
        <table class="data-table">
          <tbody>
            <tr class="table-head">
              <td>Field</td>
              <td>Type</td>
              <td>Requirement</td>
              <td>Note</td>
            </tr>
            @for (field of fields; track field.name) {
              <tr>
                <td><code>{{ field.name }}</code></td>
                <td class="muted">{{ field.type }}</td>
                <td><span class="tag">{{ field.required }}</span></td>
                <td class="muted">{{ field.note }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="grid-2 align-start">
        <div>
          <span class="eyebrow">Response status</span>
          <h2 class="section-title">Three possible outcomes</h2>
          <p class="section-lead">
            Use the status and confidence score together to decide whether to accept, decline or
            route a record to manual review.
          </p>
          <ul class="status-list">
            @for (status of statuses; track status.code) {
              <li><code>{{ status.code }}</code><span>{{ status.note }}</span></li>
            }
          </ul>
        </div>
        <div class="code-block">
          <div class="code-head">Batch request</div>
          <pre><code>{{ batchSample }}</code></pre>
        </div>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="grid-3">
        <div class="card">
          <h3>Rate limits</h3>
          <p>Generous default limits with room to grow. Enterprise plans get dedicated throughput.</p>
        </div>
        <div class="card">
          <h3>Idempotency</h3>
          <p>Send an idempotency key to safely retry requests without creating duplicate charges.</p>
        </div>
        <div class="card">
          <h3>Webhooks</h3>
          <p>Get notified when a large batch job finishes instead of polling for the result.</p>
        </div>
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="cta-band">
        <h2>Ready to integrate?</h2>
        <p>Request an API key and start verifying against fresh death data today.</p>
        <div class="actions-row center">
          <a class="btn btn-primary btn-lg" [routerLink]="[routes.contact.url]">Request API access</a>
          <a class="btn btn-outline btn-lg" [routerLink]="[routes.howItWorks.url]">See how it works</a>
        </div>
      </div>
    </div>
  </section>
</main>

<app-footer />
